class Paginate extends HTMLElement {
  settings = {};
  defaults = {
    method: 'scroll',
    container: '#product-grid',
    pagination: 'auto-paginate',
    paginationNext: '.pagination__endless',
    paginationMore: '.pagination-load-more__button',
    offset: 0,
    loadingText: 'Loading',
    callback: null,
  };

  constructor() {
    super();

    this.settings = {
      limit: this.hasAttribute('limit') ? Number(this.getAttribute('limit')) : 0,
    };

    // Merge custom configs with defaults
    this.settings = Object.assign(this.defaults, this.settings);

    // Functions
    this.addScrollListeners = this.addScrollListeners.bind(this);
    this.addClickListener = this.addClickListener.bind(this);
    this.checkIfPaginationInView = this.checkIfPaginationInView.bind(this);
    this.preventMultipleClicks = this.preventMultipleClicks.bind(this);
    this.removeClickListener = this.removeClickListener.bind(this);
    this.removeScrollListener = this.removeScrollListener.bind(this);
    this.removePaginationElement = this.removePaginationElement.bind(this);
    this.destroy = this.destroy.bind(this);

    // Selectors
    this.containerElement = document.querySelector(this.settings.container);
    this.paginationElement = document.querySelector(this.settings.pagination);
    this.paginationNextElement = document.querySelector(this.settings.paginationNext);
    this.paginationMoreElement = document.querySelector(this.settings.paginationMore);
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && ''.concat(searchParams)}`);
  }

  _withNativeView(url) {
    if (document.documentElement.dataset.shopifyNativeFallback !== '1') return url;
    try {
      const next = new URL(url, window.location.origin);
      next.searchParams.set('view', 'native');
      return next.href;
    } catch (_) {
      return url;
    }
  }

  connectedCallback() {
    if (!this.containerElement) {
      return;
    }

    this.currentPage = this.hasAttribute('current') ? Number(this.getAttribute('current')) : 1;

    const initializers = {
      click: this.addClickListener,
      scroll: this.addScrollListeners,
    };

    // initializers[this.settings.method]();
    initializers.click();
    initializers.scroll();
  }

  addScrollListeners() {
    if (!this.paginationNextElement) {
      return;
    }

    document.addEventListener('scroll', this.checkIfPaginationInView);
    window.addEventListener('resize', this.checkIfPaginationInView);
    window.addEventListener('orientationchange', this.checkIfPaginationInView);
  }

  addClickListener() {
    if (!this.paginationNextElement) {
      return;
    }

    this.nextPageLinkElement = this.paginationNextElement.querySelector('a');
    this.clickActive = true;

    if (typeof this.nextPageLinkElement !== 'undefined' && this.nextPageLinkElement !== null) {
      this.nextPageLinkElement.addEventListener('click', this.preventMultipleClicks);
      this.paginationMoreElement?.addEventListener('click', this.preventMultipleClicks);
    }
  }

  preventMultipleClicks(event) {
    event.preventDefault();

    if (!this.clickActive) {
      return;
    }

    this.nextPageUrl = this.nextPageLinkElement.href;
    this.clickActive = false;

    this.loadMore(this._withNativeView(this.nextPageUrl));
  }

  checkIfPaginationInView() {
    const top = this.paginationNextElement.getBoundingClientRect().top - this.settings.offset;
    const bottom = this.paginationNextElement.getBoundingClientRect().bottom + this.settings.offset;

    if (top <= window.innerHeight && bottom >= 0) {
      this.nextPageLinkElement = this.paginationNextElement.querySelector('a');
      this.removeScrollListener();

      if (!this.settings.limit || this.currentPage >= this.settings.limit) {
        return;
      }

      if (this.nextPageLinkElement && this.nextPageLinkElement.href !== '') {
        this.nextPageUrl = this.nextPageLinkElement.href;
        this.loadMore(this._withNativeView(this.nextPageUrl));
      }
    }
  }

  loadMore(url, updateURLHash = true) {
    this.paginationMoreElement.classList.toggle('loading');
    this.loadingSpinnerElement = this.paginationMoreElement.querySelector('.loading-overlay__spinner');
    if (this.loadingSpinnerElement) {
      this.loadingSpinnerElement.classList.toggle('hidden');
    }

    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const newContainer = html.querySelectorAll(this.settings.container)[0];
        const newPagination = html.querySelectorAll(this.settings.pagination)[0];
        const newPaginationNext = html.querySelectorAll(this.settings.paginationNext)[0];
        window.updateWishlistStateImmediately?.(newContainer);
        this.containerElement.insertAdjacentHTML('beforeend', newContainer.innerHTML);

        const browseContainer = this.containerElement.closest('[data-cnstrc-browse], [data-cnstrc-search]');
        if (browseContainer) {
          const nextPage = new URL(url).searchParams.get('page') || 2;
          browseContainer.setAttribute('data-cnstrc-result-page', nextPage);
        }

        this.paginationElement.replaceWith(newPagination);

        if (typeof newPaginationNext === 'undefined') {
          this.removePaginationElement();
        } else {
          this.paginationNextElement.innerHTML = newPaginationNext.innerHTML;

          if (this.settings.callback && typeof this.settings.callback === 'function') {
            this.settings.callback(html);
          }
        }

        if (updateURLHash) {
          const publicUrl = new URL(url, window.location.origin);
          publicUrl.searchParams.delete('view');
          Paginate.updateURLHash(publicUrl.search);
        }
      });
  }

  removeClickListener() {
    this.nextPageLinkElement.removeEventListener('click', this.preventMultipleClicks);
    this.paginationMoreElement.removeEventListener('click', this.preventMultipleClicks);
  }

  removePaginationElement() {
    this.paginationNextElement.innerHTML = '';
    this.destroy();
  }

  removeScrollListener() {
    document.removeEventListener('scroll', this.checkIfPaginationInView);
    window.removeEventListener('resize', this.checkIfPaginationInView);
    window.removeEventListener('orientationchange', this.checkIfPaginationInView);
  }

  destroy() {
    const destroyers = {
      click: this.removeClickListener,
      scroll: this.removeScrollListener,
    };

    // destroyers[this.settings.method]();
    destroyers.click();
    destroyers.scroll();

    return this;
  }
}

if (!customElements.get('auto-paginate')) {
  customElements.define('auto-paginate', Paginate);
}
